import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import { BRAND } from "@/brand";
import { Clock, ArrowLeft, ArrowRight, ShieldCheck, Calendar } from "lucide-react";

const REVIEWER = {
  name: "Sarah Lin",
  title: "Head of Operations",
  bio: "Sarah runs verification and payouts at GC SPARK. Every editorial piece is fact-checked against our internal data and current customer-support tickets before it goes live.",
};

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export default function BlogDetailPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => setData(r.data));
  }, [slug]);
  if (!data) return <div className="max-w-3xl mx-auto px-5 py-20 text-[#9a93a6]">Loading…</div>;
  const { post, related } = data;

  const html = post.content
    .split("\n")
    .map((line) => {
      if (line.startsWith("## ")) return `<h2>${line.slice(3)}</h2>`;
      if (line.startsWith("### ")) return `<h3>${line.slice(4)}</h3>`;
      if (line.startsWith("- ")) return `<li>${line.slice(2)}</li>`;
      if (line.trim() === "") return "<br/>";
      return `<p>${line}</p>`;
    })
    .join("");

  const authorName = post.author_name || "GC SPARK Editorial";
  const authorTitle = post.author_title || "Editorial";
  const authorBio = post.author_bio || "";

  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.published_at,
    dateModified: post.updated_at || post.published_at,
    image: post.hero_image_url,
    author: {
      "@type": "Person",
      name: authorName,
      jobTitle: authorTitle,
      ...(authorBio ? { description: authorBio } : {}),
      worksFor: { "@type": "Organization", name: BRAND.name },
    },
    reviewedBy: {
      "@type": "Person",
      name: REVIEWER.name,
      jobTitle: REVIEWER.title,
      description: REVIEWER.bio,
      worksFor: { "@type": "Organization", name: BRAND.name },
    },
    publisher: {
      "@type": "Organization",
      name: BRAND.name,
      logo: { "@type": "ImageObject", url: "/favicon-256.png" },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": `https://${BRAND.domain}/blog/${post.slug}` },
  };

  return (
    <>
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        type="article"
        image={post.hero_image_url}
        article={{
          author: authorName,
          publishedAt: post.published_at,
          modifiedAt: post.updated_at || post.published_at,
          section: post.category,
          tags: post.tags,
        }}
        breadcrumb={[
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: post.title, path: `/blog/${post.slug}` },
        ]}
        schema={schema}
      />
      <article className="max-w-3xl mx-auto px-5 md:px-8 py-14 md:py-20">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-[#9a93a6] hover:text-[#0b0d17]" data-testid="blog-back">
          <ArrowLeft className="w-4 h-4" /> Back to blog
        </Link>
        <div className="mt-6 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#346af8]">
          {post.category}
          <span className="text-[#9a93a6]">·</span>
          <span className="text-[#9a93a6] inline-flex items-center gap-1">
            <Clock className="w-3 h-3" /> {post.read_time} min read
          </span>
        </div>
        <h1 className="font-display text-3xl md:text-5xl font-bold text-[#0b0d17] mt-3 tracking-tight leading-tight" data-testid="blog-title">
          {post.title}
        </h1>
        <p className="mt-4 text-lg text-[#6c6376] leading-relaxed">{post.excerpt}</p>

        {/* Byline + reviewed-by + dates */}
        <div className="mt-6 bg-white border border-[#e8e8ef] rounded-2xl p-4 md:p-5 flex flex-wrap items-center gap-x-6 gap-y-3 soft-shadow" data-testid="blog-byline">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-[#346af8] text-white inline-flex items-center justify-center font-bold text-sm">
              {authorName.split(" ").map((s) => s[0]).join("").slice(0, 2)}
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-[#9a93a6]">
                Written by
              </div>
              <div className="text-[#0b0d17] font-semibold text-sm leading-tight">{authorName}</div>
              <div className="text-[#9a93a6] text-xs leading-tight">{authorTitle}</div>
            </div>
          </div>
          <div className="hidden md:block w-px h-10 bg-[#e8e8ef]" />
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-[#0b0d17]/8 text-[#0b0d17] inline-flex items-center justify-center">
              <ShieldCheck className="w-4 h-4" />
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-[#9a93a6]">
                Reviewed by
              </div>
              <div className="text-[#0b0d17] font-semibold text-sm leading-tight">{REVIEWER.name}</div>
              <div className="text-[#9a93a6] text-xs leading-tight">{REVIEWER.title}</div>
            </div>
          </div>
          <div className="hidden md:block w-px h-10 bg-[#e8e8ef]" />
          <div className="flex flex-col text-xs text-[#9a93a6]">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="w-3 h-3" />
              Published <strong className="text-[#0b0d17] font-semibold">{formatDate(post.published_at)}</strong>
            </span>
            {post.updated_at && post.updated_at !== post.published_at && (
              <span className="inline-flex items-center gap-1.5 mt-1">
                <Calendar className="w-3 h-3" />
                Updated <strong className="text-[#0b0d17] font-semibold">{formatDate(post.updated_at)}</strong>
              </span>
            )}
          </div>
        </div>

        {post.hero_image_url && (
          <figure className="mt-8 -mx-5 md:-mx-0 md:rounded-2xl overflow-hidden">
            <img
              src={post.hero_image_url}
              alt={post.title}
              className="w-full aspect-[16/9] object-cover bg-[#eaf0ff]"
              loading="eager"
            />
          </figure>
        )}
        <div
          data-testid="blog-content"
          className="prose-content mt-8 text-[#0b0d17] [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:mt-8 [&_h2]:mb-3 [&_h3]:font-display [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:text-[#6c6376] [&_p]:leading-relaxed [&_p]:mb-3 [&_li]:text-[#6c6376] [&_li]:list-disc [&_li]:ml-5"
          dangerouslySetInnerHTML={{ __html: html }}
        />

        {/* Author bio at the end (E-E-A-T signal) */}
        {authorBio && (
          <aside className="mt-12 bg-[#f4f4f8] border border-[#e8e8ef] rounded-2xl p-5 md:p-6" data-testid="blog-author-bio">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-[#9a93a6]">About the author</div>
            <div className="mt-2 flex items-start gap-4">
              <div className="shrink-0 w-12 h-12 rounded-full bg-[#346af8] text-white inline-flex items-center justify-center font-bold">
                {authorName.split(" ").map((s) => s[0]).join("").slice(0, 2)}
              </div>
              <div>
                <div className="font-display font-semibold text-[#0b0d17]">{authorName}</div>
                <div className="text-xs text-[#9a93a6]">{authorTitle} · {BRAND.name}</div>
                <p className="text-sm text-[#6c6376] mt-2 leading-relaxed">{authorBio}</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-[#e8e8ef] text-xs text-[#9a93a6] leading-relaxed">
              <strong className="text-[#0b0d17]">Editorial process:</strong> Every article on GC SPARK is reviewed by <strong className="text-[#0b0d17]">{REVIEWER.name}, {REVIEWER.title}</strong>, against our internal payout data and the latest customer-support patterns before publication.
            </div>
          </aside>
        )}

        {related?.length > 0 && (
          <div className="mt-16 pt-10 border-t border-[#e8e8ef]">
            <h3 className="font-display text-2xl font-semibold text-[#0b0d17] mb-5">Related reads</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to={`/blog/${r.slug}`}
                  data-testid={`blog-related-${r.slug}`}
                  className="bg-white border border-[#e8e8ef] rounded-xl p-5 hover:border-[#346af8] hover:shadow-lg transition-all group"
                >
                  <div className="text-xs font-semibold uppercase tracking-wider text-[#346af8]">
                    {r.category}
                  </div>
                  <div className="font-display font-semibold text-[#0b0d17] mt-1 group-hover:text-[#346af8]">
                    {r.title}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
        <div className="mt-12 rounded-2xl p-6 text-white" style={{ background: "linear-gradient(135deg, #346af8 0%, #2554d6 100%)", boxShadow: "0 24px 48px -24px rgba(52,106,248,.5)" }}>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider mb-2" style={{ background: "rgba(255,255,255,.15)" }}>
            <span style={{ color: "#ffc947" }}>★</span> Sell a card
          </div>
          <h3 className="font-display text-xl font-semibold">Ready to cash in?</h3>
          <p className="text-sm text-white/85 mt-2">Get a quote on your gift card in about three minutes.</p>
          <Link
            to="/submit"
            className="mt-4 inline-flex items-center gap-1.5 h-11 px-5 rounded-full bg-white text-[#0b0d17] font-semibold hover:bg-[#ffc947] transition-colors"
            data-testid="blog-cta-submit"
          >
            Sell now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </article>
    </>
  );
}
